// convert-configure-modal.ts — the epic import/skip MODAL behind the
// `Configure` button (#4246 P5 of docs/plans/4246-corpus-upgrade-in-console.md).
//
// The owner, 2026-09-18 17:45Z, verbatim: "when checked, a Configure button
// beside it opens a modal listing the detected epics in the branch, each
// import/skip".
//
// ── WHAT THIS MODULE IS NOT ───────────────────────────────────────────────
// It is not a second producer of the create POST body, and that is the
// property this phase is judged on. `convertRequestBody` in convert-corpus.ts
// stays THE one producer: everything here mutates exactly one thing — the
// set of epics the user UNCHECKED — and hands it back through `onChange`.
// The create dialog feeds that set into `convertPanelVm`, which feeds
// `convertRequestBody`, which is the same path the inline list rode before
// this file existed. With the same selection the body is byte-identical, and
// with the toggle OFF it is still an object with NO KEYS. A modal that
// assembled its own `{ convert_corpus: ... }` would pass every behavioural
// test here and change what the server receives.
//
// ── THE PRIMITIVE IS REUSED, NOT REBUILT ──────────────────────────────────
// The overlay is the console's existing dialog primitive —
// `openDialog` / `stackHostFor` from helm-console/src/client/dialog.ts, which
// re-export pi-stack/src/client/modal-host.ts. `stackHostFor` is the reason
// there is no content swap: the create form STAYS MOUNTED underneath and this
// dialog opens on the one stacked sibling overlay above it, where only the
// topmost host acts on Escape and Tab. Writing a second bespoke modal in a
// tree that already runs one engine is the defect modal-host.ts exists to
// have removed (it replaced six hand-rolled hosts).
//
// ── THE ROWS ARE MOVED, NOT RE-JUDGED ─────────────────────────────────────
// Every row semantic here comes from `convertPanelVm`: `checked`, `disabled`,
// `state` and `reason`. An UNSUPPORTED row is fixed at SKIP and says why in
// visible text — the converter refuses an unmapped status before writing a
// byte, so offering the choice would be offering a choice that can only fail.
// The group keeps the id, `role=group` and `aria-label` the inline list had,
// and the `.hc-convert-epics` / `.hc-convert-ready` class pair, because
// rail-registry.ts's `.hc-convert-epics.hc-convert-ready` seat addresses the
// checklist by that compound selector — the list MOVED, its scroll seat did
// not change.
//
// ── CANCEL IS A RESTORE, ON EVERY EXIT PATH ───────────────────────────────
// Flips apply LIVE, so the summary beside the button counts up while the
// modal is open. The open-time set is snapshotted, and anything that is not
// an explicit `Done` — the Cancel button, Escape, a scrim click — restores it
// through the SAME `onChange` the flips ride. One restore path, not three.

import { openDialog, stackHostFor, type DialogHandle } from './dialog.js';
import { mkIconBtn } from './buttons.js';
import { mkFcToggle } from './form-controls.js';
import type { ConvertPanelVm } from './convert-corpus.js';

/** The button's name — its `aria-label` and its tooltip, one string. */
export const CONFIGURE_BTN_LABEL = 'Configure';

/** The modal's title line. */
export const CONFIGURE_MODAL_TITLE = 'Epics to import';

/** The epic group's id and accessible name. Both MOVED here verbatim from
 * the inline list in create-dialog.ts — a screen reader that knew the group
 * by this name still finds it, one layer further in. */
export const EPICS_GROUP_ID = 'hcConvertEpics';
export const EPICS_GROUP_LABEL = 'epics to convert';

/** Why the button cannot be pressed while the checkbox is off. The modal
 * configures the upgrade; with no upgrade asked for there is nothing to
 * configure, and an enabled button that opens an empty list is a worse
 * answer than a disabled one that says this. */
export const CONFIGURE_OFF_REASON =
  'Tick “Upgrade corpus” first — there is nothing to configure until the upgrade is on.';

export interface ConfigureButtonVm {
  enabled: boolean;
  /** Null when enabled; otherwise the sentence the button carries as its
   * `title`, so a disabled control always says why. */
  reason: string | null;
}

/**
 * The button's whole enable rule, pure.
 *
 * TWO inputs, deliberately. `vm.state === 'list'` already implies the toggle
 * is on (the OFF vm is state 'off'), but the rule the owner asked for is "the
 * checkbox is ON **and** there is a list", and a rule written as one input
 * cannot be shown to test the other. `checkboxOn` is the dialog's own
 * `convertOn`, which is what the user actually ticked.
 */
export function configureButtonVm(vm: ConvertPanelVm, checkboxOn: boolean): ConfigureButtonVm {
  if (!checkboxOn || vm.state === 'off') {
    return { enabled: false, reason: CONFIGURE_OFF_REASON };
  }
  if (vm.state !== 'list') {
    return {
      enabled: false,
      reason: vm.message ?? vm.submitReason
        ?? `the epic list for ${vm.ref || '(no branch selected)'} is not ready`,
    };
  }
  return { enabled: true, reason: null };
}

/**
 * The summary span beside the button: `K of N epics to import`.
 *
 * Empty string when there is no list — an empty seat rather than "0 of 0",
 * which would read as a measured answer about a branch nothing has read.
 */
export function configureSummary(vm: ConvertPanelVm): string {
  if (vm.state !== 'list') return '';
  return `${vm.counts.selected} of ${vm.counts.total} epics to import`;
}

export interface ConfigureModalOpts {
  /** The panel vm, RE-READ after every flip — so the rows, the counts and
   * the footer are one judgment made in one place, never a copy this module
   * keeps in step by hand. */
  vm: () => ConvertPanelVm;
  /** The unchecked set as it stood when the modal opened. This is what
   * Cancel, Escape and a scrim click restore. */
  unchecked: readonly string[];
  /** Applied LIVE on every flip, and once more with the open-time snapshot
   * on any exit that is not `Done`. */
  onChange: (unchecked: string[]) => void;
  /** Fired once the modal has fully closed, by any path. */
  onClose?: () => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls?: string, text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

/**
 * Open the import/skip modal on the stacked overlay above `base` (the create
 * dialog's own host). Returns the primitive's handle so the caller can close
 * it when the panel underneath stops offering a list.
 */
export function openConfigureModal(base: HTMLElement, opts: ConfigureModalOpts): DialogHandle {
  const snapshot = [...opts.unchecked];
  const draft = new Set(snapshot);
  let committed = false;

  return openDialog(stackHostFor(base), (body, close) => {
    body.appendChild(el('h3', 'hc-dialog-title', CONFIGURE_MODAL_TITLE));
    body.appendChild(el(
      'p', 'hc-create-hint',
      'Every convertible epic is imported by default. Switch one off to skip it.',
    ));

    const group = el('div', 'hc-convert-epics');
    group.id = EPICS_GROUP_ID;
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', EPICS_GROUP_LABEL);
    body.appendChild(group);

    // The panel's own measured line — never a bare OK, and never a second
    // count computed here: it IS `vm.measured`, byte for byte.
    const footer = el('p', 'hc-convert-measured');
    footer.id = 'hcConfigureMeasured';
    footer.setAttribute('aria-live', 'polite');
    body.appendChild(footer);

    const paint = (): void => {
      const vm = opts.vm();
      footer.textContent = vm.measured;
      group.replaceChildren();
      if (vm.rows.length === 0) {
        group.classList.remove('hc-convert-ready');
        return;
      }
      group.classList.add('hc-convert-ready');
      for (const row of vm.rows) {
        const rowEl = el('label', `hc-convert-epic hc-convert-${row.state}`);
        rowEl.dataset.epic = row.epic;
        rowEl.dataset.state = row.state;
        // The per-epic import/skip control is the TOGGLE primitive
        // (mkFcToggle, role=switch) — the primitive-catalog ruling, and the
        // same control the inline list rode. ON = import, OFF = skip.
        const box = mkFcToggle({
          label: row.epic,
          text: '',
          checked: row.checked,
          disabled: row.disabled,
          onChange: (on) => {
            if (on) draft.delete(row.epic);
            else draft.add(row.epic);
            opts.onChange([...draft]);
            paint();
          },
        });
        box.el.dataset.epic = row.epic;
        box.el.setAttribute?.('name', 'convert_epic');
        box.el.setAttribute?.('value', row.epic);
        if (row.disabled) box.el.setAttribute?.('aria-disabled', 'true');
        rowEl.appendChild(box.el);
        rowEl.appendChild(el('span', 'hc-convert-epic-name', row.epic));
        if (row.reason) {
          // Visible text, not a tooltip: an unsupported row that merely looks
          // unticked is the misread this row exists to prevent.
          const why = el('span', 'hc-convert-epic-why', 'unsupported');
          why.dataset.tip = row.reason;
          why.setAttribute('aria-label', row.reason);
          rowEl.appendChild(why);
        }
        group.appendChild(rowEl);
      }
    };
    paint();

    const actions = el('div', 'hc-dialog-actions');
    const cancel = mkIconBtn({
      glyph: 'x', label: 'Cancel', id: 'hcConfigureCancel',
      attrs: { 'data-act': 'no' },
    });
    cancel.addEventListener('click', () => { close(); });
    const done = mkIconBtn({
      glyph: 'check', label: 'Done', id: 'hcConfigureDone',
      attrs: { 'data-act': 'yes' },
    });
    done.addEventListener('click', () => { committed = true; close(); });
    // Cancel first in DOM, the primary action rightmost — the confirmation
    // layout spec dialog.ts documents.
    actions.appendChild(cancel);
    actions.appendChild(done);
    body.appendChild(actions);
  }, {
    className: 'hc-convert-configure',
    onClose: () => {
      // EVERY exit that is not Done restores — the Cancel button, Escape and
      // a scrim click all land here, so there is one restore path rather than
      // three that have to be kept in step.
      if (!committed) opts.onChange([...snapshot]);
      opts.onClose?.();
    },
  });
}
